import React, { useState, useEffect, useContext, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, Brain, Dumbbell, Sparkles, 
  RefreshCw, Zap, Target,
  ChevronLeft, ChevronRight, CalendarDays
} from "lucide-react";
import { toast } from "sonner";
import { ExerciseItem } from "../components/TrainingWidgets";
import { devError } from "../utils/devLogger";

const TrainingPlan = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const isToday = selectedDate === new Date().toISOString().split("T")[0];

  const fetchPlanForDate = useCallback(async (date) => {
    setLoading(true);
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const lang = i18n.language || "en";
      const endpoint = date === todayStr
        ? `${API}/training/today?language=${lang}`
        : `${API}/training/plan/${date}`;
      const response = await axios.get(endpoint);
      setPlan(response.data.plan);
      setCompletedExercises(response.data.completed_exercises || []);
      // Auto-regenerate if the plan was generated in a different language
      if (date === todayStr && response.data.language_mismatch && response.data.plan) {
        generatePlanSilent(lang);
      }
    } catch (error) {
      devError("Failed to fetch plan:", error);
      setPlan(null);
      setCompletedExercises([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchPlanForDate(selectedDate);
  }, [selectedDate, fetchPlanForDate]);

  const navigateDate = (direction) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + direction);
    const todayStr = new Date().toISOString().split("T")[0];
    const newDate = d.toISOString().split("T")[0];
    // Don't go into the future, max 7 days back
    if (newDate > todayStr) return;
    const daysBack = Math.floor((new Date(todayStr) - d) / (1000 * 60 * 60 * 24));
    if (daysBack > 7) return;
    setSelectedDate(newDate);
  };

  const generatePlanSilent = async (lang) => {
    try {
      const response = await axios.post(`${API}/training/generate`, { language: lang });
      setPlan(response.data);
      setCompletedExercises([]);
    } catch (error) {
      devError("Silent regeneration failed:", error);
    }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/training/generate`, { language: i18n.language });
      setPlan(response.data);
      setCompletedExercises([]);
      toast.success(t('notifications.planReady'));
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('training.completeCheckinFirst', 'Please complete your daily check-in first'));
        navigate('/questionnaire');
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const toggleExercise = async (exerciseId, exerciseType) => {
    const isCompleted = completedExercises.includes(exerciseId);
    
    try {
      const response = await axios.post(`${API}/progress/log`, {
        exercise_id: exerciseId,
        exercise_type: exerciseType,
        completed: !isCompleted,
        date: isToday ? undefined : selectedDate
      });

      if (isCompleted) {
        setCompletedExercises(prev => prev.filter(id => id !== exerciseId));
      } else {
        setCompletedExercises(prev => [...prev, exerciseId]);
        toast.success(t('notifications.exerciseCompleted'));
        
        if (response.data.new_achievements && response.data.new_achievements.length > 0) {
          response.data.new_achievements.forEach(achievement => {
            setTimeout(() => {
              toast.success(t('notifications.achievementUnlocked', { name: achievement.name }), {
                duration: 5000
              });
            }, 1000);
          });
        }
      }
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const physicalCompleted = plan 
    ? completedExercises.filter(id => plan.physical_exercises.some(e => e.id === id)).length
    : 0;
  
  const cognitiveCompleted = plan
    ? completedExercises.filter(id => plan.cognitive_exercises.some(e => e.id === id)).length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="training-plan-page">
      {/* Generating Overlay */}
      {generating && createPortal(
        <div className="fixed inset-0 z-[100] bg-sky-50/90 backdrop-blur-sm flex items-center justify-center" data-testid="generating-overlay">
          <div className="glass-card p-8 text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-sky-400 to-indigo-400 flex items-center justify-center animate-pulse">
              <Sparkles className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-medium text-sky-800 mb-2" style={{ fontFamily: 'Outfit' }}>
              {t('training.generating.title')}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {t('training.generating.subtitle')}
            </p>
            <div className="w-full h-2 bg-sky-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-sky-400 to-indigo-400 rounded-full animate-loading-bar" />
            </div>
          </div>
        </div>,
        document.body
      )}
      <div className="fixed top-0 right-0 w-96 h-96 bg-cyan-300/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-1 sm:mr-2" strokeWidth={1.5} />
              <span className="hidden sm:inline">{t('common.dashboard')}</span>
            </Button>
            
            {/* Date Navigation */}
            <div className="flex items-center gap-1 sm:gap-2" data-testid="date-navigator">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigateDate(-1)}
                className="text-sky-600 h-8 w-8"
                data-testid="prev-date-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/50 border border-sky-100">
                <CalendarDays className="w-4 h-4 text-sky-500" strokeWidth={1.5} />
                <span className="text-sm font-medium text-sky-800">
                  {isToday ? t('common.today') : new Date(selectedDate + "T12:00:00").toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
                {!isToday && (
                  <span className="text-xs text-amber-500 ml-1">(past)</span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigateDate(1)}
                className="text-sky-600 h-8 w-8"
                disabled={isToday}
                data-testid="next-date-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Button
              variant="ghost"
              onClick={generatePlan}
              disabled={generating || !isToday}
              className="text-sky-600 hover:text-sky-800"
              data-testid="regenerate-plan-btn"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${generating ? 'animate-spin' : ''}`} strokeWidth={1.5} />
              {t('training.regenerate')}
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-500 mb-2">
              {new Date(selectedDate + "T12:00:00").toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
            <h1 className="text-3xl sm:text-4xl font-light text-sky-900" style={{ fontFamily: 'Outfit' }}>
              {isToday ? t('training.title') : t('training.title') + ' — Past'}
            </h1>
            {!isToday && (
              <p className="text-sm text-amber-600 mt-1 flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4" strokeWidth={1.5} />
                You can retroactively mark exercises as complete for this day
              </p>
            )}
          </div>

          {!plan ? (
            <div className="glass-card p-12 text-center" data-testid="no-plan-card">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-sky-100 flex items-center justify-center">
                <Target className="w-10 h-10 text-sky-400" strokeWidth={1.5} />
              </div>
              <h2 className="text-xl font-medium text-sky-800 mb-2" style={{ fontFamily: 'Outfit' }}>
                {isToday ? t('training.noPlan.title') : 'No plan found for this day'}
              </h2>
              <p className="text-slate-500 mb-6">
                {isToday ? t('training.noPlan.description') : 'No training plan was generated for this date.'}
              </p>
              {isToday && (
              <Button
                onClick={() => navigate('/questionnaire')}
                className="glow-button"
                data-testid="start-checkin-btn"
              >
                {t('dashboard.startCheckin')}
              </Button>
              )}
            </div>
          ) : (
            <>
              <div className="glass-card p-6 mb-8 border-l-4 border-sky-400" data-testid="ai-recommendation">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-sky-400/20 flex items-center justify-center flex-shrink-0 glow-pulse">
                    <Zap className="w-5 h-5 text-sky-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-sky-800 mb-1">{t('dashboard.aiRecommendation')}</h4>
                    <p className="text-slate-600">{plan.ai_recommendation}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <div className="glass-card p-6" data-testid="physical-exercises-card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/20 flex items-center justify-center">
                      <Dumbbell className="w-6 h-6 text-cyan-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h2 className="text-xl font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>
                        {t('training.physical')}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {physicalCompleted} / {plan.physical_exercises.length} {t('training.completed')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {plan.physical_exercises.map((exercise, idx) => (
                      <ExerciseItem
                        key={exercise.id}
                        exercise={exercise}
                        isCompleted={completedExercises.includes(exercise.id)}
                        onToggle={toggleExercise}
                        type="physical"
                        index={idx}
                        t={t}
                      />
                    ))}
                  </div>
                </div>

                <div className="glass-card p-6" data-testid="cognitive-exercises-card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-indigo-300/30 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-indigo-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h2 className="text-xl font-medium text-sky-800" style={{ fontFamily: 'Outfit' }}>
                        {t('training.cognitive')}
                      </h2>
                      <p className="text-sm text-slate-500">
                        {cognitiveCompleted} / {plan.cognitive_exercises.length} {t('training.completed')}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {plan.cognitive_exercises.map((exercise, idx) => (
                      <ExerciseItem
                        key={exercise.id}
                        exercise={exercise}
                        isCompleted={completedExercises.includes(exercise.id)}
                        onToggle={toggleExercise}
                        type="cognitive"
                        index={idx}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 glass-card p-6 text-center" data-testid="summary-card">
                <div className="flex items-center justify-center gap-8">
                  <div>
                    <p className="text-3xl font-light text-cyan-500" style={{ fontFamily: 'Outfit' }}>
                      {physicalCompleted}/{plan.physical_exercises.length}
                    </p>
                    <p className="text-sm text-slate-500">{t('training.summary.physical')}</p>
                  </div>
                  <div className="w-px h-12 bg-sky-200" />
                  <div>
                    <p className="text-3xl font-light text-indigo-500" style={{ fontFamily: 'Outfit' }}>
                      {cognitiveCompleted}/{plan.cognitive_exercises.length}
                    </p>
                    <p className="text-sm text-slate-500">{t('training.summary.cognitive')}</p>
                  </div>
                  <div className="w-px h-12 bg-sky-200" />
                  <div>
                    <p className="text-3xl font-light text-sky-500" style={{ fontFamily: 'Outfit' }}>
                      {Math.round(((physicalCompleted + cognitiveCompleted) / (plan.physical_exercises.length + plan.cognitive_exercises.length)) * 100)}%
                    </p>
                    <p className="text-sm text-slate-500">{t('training.summary.complete')}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default TrainingPlan;
